import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, EmptyState, Icon, Table, TableSkeletonRows, toast, type BadgeTone } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { datedFilename, saveBlob } from "../../engage/shared/download";
import { useBuildFormatters } from "../shared/format";
import type { ActionRun } from "../shared/types";
import { useExportRuns } from "./use-actions";

const COLUMNS = 5;
const TONES: Readonly<Record<string, BadgeTone>> = { succeeded: "success", failed: "danger", timed_out: "danger", cancelled: "neutral", pending_confirmation: "warning", expired: "neutral", running: "brand" };

function StatusCell({ run }: { run: ActionRun }) {
  const { t } = useTranslation();
  const ok = run.status === "succeeded";
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Badge tone={TONES[run.status] ?? "neutral"} dot>
        {t(`build.actions.runs.statuses.${run.status}`, { defaultValue: run.status })}
      </Badge>
      {!ok && run.error_summary ? <span className="text-xs text-fg-subtle">{run.error_summary}</span> : null}
    </span>
  );
}

/** "Recent action runs" (last 8) + the server-side CSV export of every run of the last 7 days. */
export function RunsTable({ agentId, runs, isPending }: { agentId: string; runs: readonly ActionRun[] | undefined; isPending: boolean }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const fmt = useBuildFormatters();
  const message = useErrorMessage();
  const exportRuns = useExportRuns(agentId);
  const exportCsv = () => {
    exportRuns.mutate(undefined, {
      onSuccess: (csv) => {
        const name = datedFilename("action-runs", "csv");
        saveBlob(csv, name);
        toast.success(t("build.actions.runs.exported", { name }));
      },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <section className="card overflow-hidden" aria-labelledby="action-runs-title">
      <div className="card-header">
        <div>
          <h2 id="action-runs-title" className="card-title">{t("build.actions.runs.title")}</h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("build.actions.runs.subtitle")}</p>
        </div>
        <Button variant="ghost" size="sm" loading={exportRuns.isPending} disabled={!runs?.length} aria-describedby="action-runs-export-hint" onClick={exportCsv}>
          <Icon name="download" />
          {t("build.actions.runs.export")}
        </Button>
        <span id="action-runs-export-hint" className="sr-only">{t("build.actions.runs.exportHint")}</span>
      </div>
      {runs?.length === 0 ? (
        <EmptyState icon="activity" headingLevel={3} title={t("build.actions.runs.empty")} description={t("build.actions.runs.emptyBody")} />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <th>{t("build.actions.runs.time")}</th>
                <th>{t("build.actions.runs.action")}</th>
                <th>{t("build.actions.runs.conversation")}</th>
                <th>{t("build.actions.runs.status")}</th>
                <th className="text-right">{t("build.actions.runs.duration")}</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? <TableSkeletonRows rows={4} columns={COLUMNS} /> : null}
              {runs?.map((run) => (
                <tr key={run.id}>
                  <td className="whitespace-nowrap tabular-nums text-fg-muted">{fmt.dateTime(run.created_at)}</td>
                  <td className="font-semibold">{run.action_name}</td>
                  <td>
                    {run.conversation_id ? (
                      <AppLink href={consoleRoutes.inbox(summary.slug, run.conversation_id)} className="font-semibold text-primary-ink hover:underline">
                        {t("build.actions.runs.openConversation")}
                      </AppLink>
                    ) : <span className="text-fg-subtle">{t("build.actions.runs.noConversation")}</span>}
                  </td>
                  <td><StatusCell run={run} /></td>
                  <td className="text-right font-mono text-xs">{fmt.duration(run.duration_ms)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </section>
  );
}
